// src/TermsOfService.jsx

const TermsOfService = ({ onBack }) => {
  const lastUpdated = "March 25, 2026";

  return (
    <section style={{
      padding: "clamp(64px, 10vh, 120px) 24px 80px",
      maxWidth: "720px", margin: "0 auto",
    }}>
      <button
        onClick={onBack}
        aria-label="Go back"
        style={{
          background: "none", border: "none", cursor: "pointer",
          fontSize: "14px", color: "var(--ink-muted)", fontFamily: "inherit",
          marginBottom: "32px", padding: 0,
        }}
        onMouseEnter={(e) => e.target.style.color = "var(--ink)"}
        onMouseLeave={(e) => e.target.style.color = "var(--ink-muted)"}
      >
        &larr; Back
      </button>

      <h1 style={{
        fontFamily: "'DM Serif Display', serif",
        fontSize: "clamp(28px, 4vw, 36px)", fontWeight: "400",
        color: "var(--ink)", letterSpacing: "-0.02em",
        marginBottom: "8px",
      }}>
        Terms of Service
      </h1>
      <p style={{
        fontSize: "13px", color: "var(--ink-muted)", marginBottom: "40px",
      }}>
        Last updated: {lastUpdated}
      </p>

      <div style={{
        fontSize: "15px", lineHeight: 1.8, color: "var(--ink-soft)",
        display: "flex", flexDirection: "column", gap: "28px",
      }}>
        {/* Medical Disclaimer — prominent */}
        <div style={{
          backgroundColor: "var(--surface-warm)",
          border: "1px solid var(--border)",
          borderRadius: "8px",
          padding: "24px",
        }}>
          <h2 style={{
            ...sh, fontSize: "20px", color: "var(--ink)", marginBottom: "12px",
          }}>
            Important Medical &amp; Legal Disclaimer
          </h2>
          <p style={{ marginBottom: "12px" }}>
            Ask Irene is an AI-powered informational tool. It is <strong style={st}>not</strong> a
            substitute for professional medical, legal, financial, or billing advice.
          </p>
          <p style={{ marginBottom: "12px" }}>
            Irene does not provide medical diagnoses, legal counsel, or guaranteed
            financial outcomes. The information provided is general in nature and
            may not apply to your specific situation. Medical billing rules,
            insurance regulations, and assistance programs vary by state, provider,
            and individual circumstance.
          </p>
          <p>
            <strong style={st}>Always consult qualified professionals</strong> — including
            licensed attorneys, certified medical billing advocates, financial
            advisors, and healthcare providers — for decisions about your specific
            situation. Do not delay seeking professional advice based on
            information provided by Ask Irene.
          </p>
        </div>

        <div>
          <h2 style={sh}>Acceptance of Terms</h2>
          <p>
            By accessing or using Ask Irene (askirene.ai), you agree to be bound by
            these Terms of Service. If you do not agree to these terms, please do
            not use the service.
          </p>
        </div>

        <div>
          <h2 style={sh}>Description of Service</h2>
          <p>
            Ask Irene is an AI-powered chat advisor that provides general
            informational guidance about medical billing, insurance claims,
            financial assistance programs, and patient rights. The service is
            available in two tiers: a free tier with basic chat functionality, and
            a paid Pro tier ($9/month) with additional features including
            conversation history and bill analysis.
          </p>
        </div>

        <div>
          <h2 style={sh}>No Professional Advice</h2>
          <p>
            The information provided by Ask Irene is for general educational and
            informational purposes only. Ask Irene does not provide and is not a
            substitute for professional medical advice, legal advice, financial
            advice, tax advice, or professional medical billing services. No
            professional-client relationship is created by your use of this service.
            You acknowledge that any actions you take based on information from
            Ask Irene are taken at your own risk.
          </p>
        </div>

        <div>
          <h2 style={sh}>Accuracy of Information</h2>
          <p>
            Ask Irene uses artificial intelligence to generate responses. While we
            strive for accuracy, AI-generated content may contain errors,
            omissions, or outdated information. Medical billing regulations,
            insurance rules, and assistance programs change frequently. We do not
            guarantee the accuracy, completeness, or timeliness of any information
            provided. You should independently verify any information before
            relying on it.
          </p>
        </div>

        <div>
          <h2 style={sh}>User Responsibilities</h2>
          <p>
            You are responsible for the information you share with Ask Irene. We
            recommend that you do not share sensitive personal identifiers such as
            Social Security numbers, insurance member IDs, medical record numbers,
            or financial account numbers. You are responsible for maintaining the
            confidentiality of your account credentials. You agree to use the
            service only for lawful purposes and in accordance with these terms.
          </p>
        </div>

        <div>
          <h2 style={sh}>Subscription and Payments</h2>
          <p>
            The Pro tier is billed monthly at $9 plus applicable taxes. Payments
            are processed securely by Stripe. You can cancel your subscription at
            any time from your Account page; cancellation takes effect at the end
            of the current billing period. We reserve the right to change pricing
            with reasonable notice. Refunds are handled on a case-by-case basis.
          </p>
        </div>

        <div>
          <h2 style={sh}>Intellectual Property</h2>
          <p>
            The Ask Irene name, logo, website design, and original content are the
            property of Ask Irene and its operators. You retain ownership of any
            personal information and conversation content you provide. We claim no
            ownership of your data.
          </p>
        </div>

        <div>
          <h2 style={sh}>Limitation of Liability</h2>
          <p>
            To the maximum extent permitted by law, Ask Irene and its operators,
            employees, and affiliates shall not be liable for any indirect,
            incidental, special, consequential, or punitive damages, including but
            not limited to loss of profits, data, or other intangible losses,
            resulting from your use of or inability to use the service, any
            decisions made based on information provided by the service, unauthorized
            access to or alteration of your data, or any other matter relating to
            the service.
          </p>
        </div>

        <div>
          <h2 style={sh}>Indemnification</h2>
          <p>
            You agree to indemnify and hold harmless Ask Irene and its operators
            from any claims, damages, losses, or expenses (including reasonable
            attorney's fees) arising from your use of the service, your violation
            of these terms, or your violation of any rights of another party.
          </p>
        </div>

        <div>
          <h2 style={sh}>Termination</h2>
          <p>
            We reserve the right to suspend or terminate your access to Ask Irene
            at any time, with or without cause, and with or without notice. Upon
            termination, your right to use the service ceases immediately. Provisions
            of these terms that by their nature should survive termination shall
            survive, including ownership provisions, warranty disclaimers,
            indemnity, and limitations of liability.
          </p>
        </div>

        <div>
          <h2 style={sh}>Governing Law</h2>
          <p>
            These terms shall be governed by and construed in accordance with the
            laws of the State of Ohio, without regard to its conflict of law
            provisions.
          </p>
        </div>

        <div>
          <h2 style={sh}>Changes to Terms</h2>
          <p>
            We may modify these Terms of Service at any time. Material changes will
            be communicated by updating the "Last updated" date. Your continued use
            of Ask Irene after changes constitutes acceptance of the modified terms.
          </p>
        </div>

        <div>
          <h2 style={sh}>Contact</h2>
          <p>
            If you have questions about these Terms of Service, please contact us
            at{" "}
            <a href="mailto:hello@askirene.ai" style={{
              color: "var(--accent)", textDecoration: "underline",
              textUnderlineOffset: "2px",
            }}>hello@askirene.ai</a>.
          </p>
        </div>
      </div>
    </section>
  );
};

const sh = {
  fontSize: "18px", fontWeight: "600", color: "var(--ink)",
  marginBottom: "8px", fontFamily: "'DM Serif Display', serif",
  letterSpacing: "-0.01em",
};

const st = { color: "var(--ink)", fontWeight: "500" };

export default TermsOfService;
